/* eslint react/prop-types: 0 */
import { Container, Row, Col, Table } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import filesize from 'filesize'
import SbExpander from '../SbExpander'
import AssociatedItems from '../AssociatedItems'
import '../../styles/FileDetails.css'
import '../../styles/SearchResults.css'
import React from 'react'
import { isEmpty } from 'lodash'
import { itemType } from '../../types'

export default function ItemView({ item }) {
  if (isEmpty(item)) return null

  return (
    <Container>
      <Row>
        <Col lg={8}>
          <DatesSection dates={item.dates} />
          <UsgsCitationSection usgsCitation={item.citation} />
          <SummarySection item={item} />
          <ContactsSection contacts={item.contacts} />
          <FilesSection item={item} />
          <RelatedExternalResourcesSection webLinks={item.webLinks} />
          <MaterialRequestInstructionsSection
            materialRequestInstructions={item.materialRequestInstructions}
          />
          <PurposeSection purpose={item.purpose} />
          <RightsSection rights={item.rights} />
          <PromotedExtensions facets={item.facets} />
        </Col>
        <Col lg={4}>
          <NonMapPreviewImageSection
            previewImageAttributes={item.previewImage}
          />
          <MapPreviewSection previewImageAttributes={item.previewImage} />
          <SpatialServicesSection spatial={item.spatial} />
          <CommunitiesSection />
          <AssociatedItemsSection itemId={item.id} />
          <TagsSection item={item} />
          <ProvenanceSection provenance={item.provenance} />
        </Col>
      </Row>

      <Row>
        <Col lg={8}>
          <AdditionalInformationSection item={item} />
        </Col>
        <Col lg={4}></Col>
      </Row>
    </Container>
  )
}

export function convertDateTime(datetime = '') {
  const dateToFormat = Date.parse(datetime)
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(dateToFormat)
}

ItemView.propTypes = {
  item: itemType,
}

// const Section = ({props}) => {
// function Section(props) {
export const Section = ({ children, id, title }) => {
  if (!children) return null
  return (
    <section id={id} className="sb-section">
      {title ? <h2 className="sb-section-header">{title}</h2> : ''}
      <div className="sb-section-body">{children}</div>
    </section>
  )
}

const SubSection = ({ children, id, title }) => {
  return (
    <div id="{id}" className="sb-subsection">
      <h3>{title}</h3>
      <div className="sb-subsection-body">{children}</div>
    </div>
  )
}

const DatesSection = ({ dates }) => {
  if (!dates || !dates.length) return null
  const visibleDates = dates.filter(
    (date) => !['dateCreated', 'lastUpdated'].includes(date.type)
  )
  if (!visibleDates.length) return null
  if (dates) {
    return (
      <Section id="dates" title="Dates">
        <dl className="dl-horizontal sb-dl-colon">
          {visibleDates.map((itemDate, i) => (
            <div key={i} className="sb-dl-colon">
              <dt>{itemDate.label ? itemDate.label : itemDate.type}</dt>
              <dd>{itemDate.dateString}</dd>
            </div>
          ))}
        </dl>
      </Section>
    )
  } else {
    return null
  }
}

const UsgsCitationSection = ({ usgsCitation }) => {
  if (!usgsCitation) return null
  return (
    <Section id="citation" title="Citation">
      {usgsCitation}
    </Section>
  )
}

const SummarySection = ({ item }) => {
  if (!item.summary) return null

  return (
    <Section id="summary" title="Summary">
      <div className="visible-xs-block">{item.summary}</div>

      <div className="hidden-xs">
        <SbExpander
          collapsedHeight={300}
          minHeightToCollapse={400}
          expanderLabel=""
        >
          <span
            dangerouslySetInnerHTML={{ __html: item.body ? item.body : null }}
          ></span>
        </SbExpander>
      </div>
    </Section>
  )
}

// const ChildrenSection = ({item}) => {
//   return (
//     <Section id="folder" title="Child Items">
//       ... TODO ...
//     </Section>
//   )
// }

const ContactsSection = ({ contacts }) => {
  if (!contacts) return null

  let contactTypesOrderedByAppearance = []
  let contactsByType = {}
  contacts.forEach((contact) => {
    if (!contactTypesOrderedByAppearance.includes(contact.type)) {
      contactTypesOrderedByAppearance.push(contact.type)
    }
    if (!contactsByType[contact.type]) {
      contactsByType[contact.type] = []
    }
    contactsByType[contact.type].push(contact)
  })

  return (
    <Section id="contacts" title="Contacts">
      <SbExpander
        collapsedHeight={200}
        minHeightToCollapse={300}
        expanderLabel={'Contacts'}
      >
        <dl className="dl-horizontal sb-dl-colon">
          {Object.keys(contactsByType).map((contactType) => (
            <div key={contactType} className="sb-dl-colon">
              <dt>
                {contactType && contactType !== 'null' ? (
                  contactType
                ) : (
                  <span className="dim">(other)</span>
                )}
              </dt>
              <dd>
                {/* TODO: links, blurbs, or something for contacts */}
                {contactsByType[contactType]
                  .map((contact) => contact.name)
                  .join(', ')}
              </dd>
            </div>
          ))}
        </dl>
      </SbExpander>
    </Section>
  )
}

// NOTE: the zip downloads in the files section has been intentionally omitted for now.
// If we want this in public search app, we'll need to come up solution that protects sciencebase from the bot downloads
// that have been in problem in the past.  The solution will probably live in the forthcoming Files app.
const FilesSection = ({ item }) => {
  const files = item.files
  const facetsWithFiles =
    item.facets && item.facets.filter((facet) => facet.files)
  if (!(files && files.length) && !(facetsWithFiles && facetsWithFiles.length))
    return null
  return (
    <Section id="attached-files" title="Attached Files">
      <SbExpander
        collapsedHeight={500}
        minHeightToCollapse={600}
        expanderLabel={'Attached Files'}
      >
        <Table responsive bordered striped>
          <tbody>
            {item.files &&
              item.files.map((itemFile, i) => (
                <ItemFileRow key={i} itemFile={itemFile} />
              ))}
          </tbody>
        </Table>
        <FacetFiles facets={item.facets} />
      </SbExpander>
    </Section>
  )
}

const FacetFiles = ({ facets }) => {
  if (!facets) return null

  return (
    <div>
      {facets
        .filter((facet) => facet.files)
        .map((facet, i) => (
          <Table key={i} responsive bordered striped>
            <caption>
              {facet.facetName === 'Shapefile' ? 'Shapefile: ' : 'Extension: '}
              {facet.name}
            </caption>
            <tbody>
              {facet.files.map((itemFile, i) => (
                <ItemFileRow key={i} itemFile={itemFile} />
              ))}
            </tbody>
          </Table>
        ))}
    </div>
  )
}

export const ItemFileRow = ({ itemFile }) => {
  if (!itemFile) return null
  const contentTypeAddendum = itemFile.contentType
    ? ` [${itemFile.contentType}]`
    : ''
  const fileHrefTitle = `${
    itemFile.title || itemFile.name || ''
  }${contentTypeAddendum}`

  let viewLink
  if (
    itemFile.viewUri &&
    itemFile.previewImage &&
    itemFile.previewImage.thumbnail
  ) {
    viewLink = (
      <a
        target="_blank"
        rel="noopener noreferrer"
        href={itemFile.metadataHtmlViewUri}
        title={'View'}
      >
        <img
          className={'sb-file-thumbnail'}
          alt={'Thumbnail'}
          src={itemFile.previewImage.thumbnail.uri}
        />
      </a>
    )
  } else if (itemFile.metadataHtmlViewUri) {
    viewLink = (
      <a
        target="_blank"
        rel="noopener noreferrer"
        href={itemFile.metadataHtmlViewUri}
        title={'View Metadata'}
      >
        <i className="fa fa-file-text" aria-hidden="true"></i>
        <br />
        View
      </a>
    )
  } else if (itemFile.viewUri) {
    viewLink = (
      <a
        target="_blank"
        rel="noopener noreferrer"
        href={itemFile.viewUri}
        title={'View File'}
      >
        <i className="glyphicon glyphicon-file"></i>
      </a>
    )
  }
  return (
    <tr>
      <td>
        <a href={itemFile.downloadUri} title={fileHrefTitle}>
          <i
            className="glyphicon glyphicon-download-alt"
            aria-hidden="true"
          ></i>
          &nbsp;
          {itemFile.name}
        </a>
        {itemFile.title ? (
          <div className="sb-file-subtitle">{itemFile.title}</div>
        ) : (
          ''
        )}
      </td>
      <td className={'text-center'}>{viewLink}</td>
      <td>{itemFile.size ? filesize(itemFile.size) : 0}</td>
    </tr>
  )
}

const RelatedExternalResourcesSection = ({ webLinks }) => {
  if (!webLinks || !webLinks.length) return null
  const visibleWebLinks = webLinks.filter((webLink) => !webLink.hidden)
  if (!visibleWebLinks.length) return null

  const visibleWebLinksGroupedByLabel = visibleWebLinks.reduce(function (
    groups,
    webLink
  ) {
    const val = webLink.typeLabel || webLink.label || 'Web Link'
    groups[val] = groups[val] || []
    groups[val].push(webLink)
    return groups
  },
  {})

  return (
    <Section id="related-external-resources" title="Related External Resources">
      {Object.keys(visibleWebLinksGroupedByLabel).map(
        (webLinkGroupLabel, i) => (
          <Table key={i} responsive bordered striped>
            <caption>Type: {webLinkGroupLabel}</caption>
            <tbody>
              {visibleWebLinksGroupedByLabel[webLinkGroupLabel].map(
                (webLink, i) => (
                  <tr key={i}>
                    <td>{webLink.title || webLink.label || 'Web Link'}</td>
                    <td>
                      <Link
                        target="_blank"
                        to={webLink.uri}
                        className="sb-break-all"
                      >
                        {webLink.uri}
                      </Link>
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </Table>
        )
      )}
    </Section>
  )
}

const MaterialRequestInstructionsSection = ({
  materialRequestInstructions,
}) => {
  if (!materialRequestInstructions) return null
  return (
    <Section
      id="material-request-instructions"
      title="Material Request Instructions"
    >
      <span
        dangerouslySetInnerHTML={{ __html: materialRequestInstructions }}
      ></span>
    </Section>
  )
}

const PurposeSection = ({ purpose }) => {
  if (!purpose) return null
  return (
    <Section id="purpose-instructions" title="Purpose">
      {purpose}
    </Section>
  )
}

const RightsSection = ({ rights }) => {
  if (!rights) return null
  return (
    <Section id="rights-instructions" title="Rights">
      {rights}
    </Section>
  )
}

const NonMapPreviewImageSection = ({ previewImageAttributes }) => {
  if (!previewImageAttributes) return null

  let previewImageForImgResponsive
  if (previewImageAttributes.medium)
    previewImageForImgResponsive = previewImageAttributes.medium
  else if (previewImageAttributes.original)
    previewImageForImgResponsive = previewImageAttributes.original
  else if (previewImageAttributes.small)
    previewImageForImgResponsive = previewImageAttributes.small
  else if (previewImageAttributes.thumbnail)
    previewImageForImgResponsive = previewImageAttributes.thumbnail

  if (!previewImageForImgResponsive) return null

  let previewImageTitle = previewImageForImgResponsive.title
    ? previewImageForImgResponsive.title
    : null

  return (
    <Section id="preview-image">
      <img
        className="img-responsive"
        alt={previewImageTitle ? previewImageTitle : 'Preview'}
        title={previewImageTitle}
        src={previewImageForImgResponsive.uri}
      />
    </Section>
  )
}

const MapPreviewSection = ({ previewImageAttributes }) => {
  if (!previewImageAttributes) return null
  if (!previewImageAttributes.alternateImages) return null

  let mapPreview = previewImageAttributes.alternateImages.find(
    (alternateImage) => alternateImage.from === 'mapPreview'
  )
  if (!mapPreview) return null

  let previewImageForImgResponsive

  if (mapPreview.medium) previewImageForImgResponsive = mapPreview.medium
  else if (mapPreview.original)
    previewImageForImgResponsive = mapPreview.original
  else if (mapPreview.small) previewImageForImgResponsive = mapPreview.small
  else if (mapPreview.thumbnail)
    previewImageForImgResponsive = mapPreview.thumbnail

  return (
    <Section id="map-preview" title="Map">
      <img
        className="img-responsive"
        alt="Map"
        src={previewImageForImgResponsive.uri}
      />
    </Section>
  )
}

const SpatialServicesSection = ({ spatial }) => {
  if (!spatial) return null
  return (
    <Section id="spatial-services" title="Spatial Services">
      {/* boundingBox
  extents
  representationalPoint
  representationalPointIsDerived */}
    </Section>
  )
}

const CommunitiesSection = () => {
  return <Section id="communities" title="Communities"></Section>
}

const AssociatedItemsSection = ({ itemId }) => {
  return <AssociatedItems itemId={itemId} />
}

const TagsSection = ({ item }) => {
  if (
    !(item.browseCategories && item.browseCategories.length) &&
    !(item.browseTypes && item.browseTypes.length) &&
    !(item.tags && item.tags.length)
  )
    return null

  let browseCategoriesEntry
  if (item.browseCategories && item.browseCategories.length) {
    browseCategoriesEntry = (
      <div className="sb-dl-tags">
        <dt>Categories</dt>
        <dd>
          <ul className="list sb-list-tags">
            {item.browseCategories.map((category, i) => (
              <li key={i}>{category}</li>
            ))}
          </ul>
        </dd>
      </div>
    )
  }

  let subjectsEntry
  if (item.subjects && item.subjects.length) {
    subjectsEntry = (
      <div className="sb-dl-tags">
        <dt>Subjects</dt>
        <dd>
          <ul className="list sb-list-tags">
            {item.subjects.map((subjects, i) => (
              <li key={i}>{subjects}</li>
            ))}
          </ul>
        </dd>
      </div>
    )
  }

  let browseTypesEntry
  if (item.browseTypes && item.browseTypes.length) {
    browseTypesEntry = (
      <div className="sb-dl-tags">
        <dt>Types</dt>
        <dd>
          <ul className="list sb-list-tags">
            {item.browseTypes.map((browseType, i) => (
              <li key={i}>{browseType}</li>
            ))}
          </ul>
        </dd>
      </div>
    )
  }

  // const visibleWebLinksGroupedByLabel = visibleWebLinks.reduce(function(groups, webLink) {
  //   var val = webLink.typeLabel || webLink.label || "Web Link";
  //   groups[val] = groups[val] || [];
  //   groups[val].push(webLink);
  //   return groups;
  // }, {});
  const tags = item.tags && item.tags.length ? item.tags : []

  const tagsByType = tags.reduce(function (groups, tag) {
    let tagType = tag.type ? tag.type : 'Other'
    groups[tagType] = groups[tagType] || []
    groups[tagType].push(tag)
    return groups
  }, {})

  const tagsByTypeEntries = Object.keys(tagsByType).map((tagType, i) => {
    return (
      <div key={i} className="sb-dl-tags">
        <dt>{tagType}</dt>
        <dd>
          <ul className="list sb-list-tags">
            {tagsByType[tagType].map((tag, i) => (
              <li key={i}>
                <Link
                  to={{
                    pathname: '/',
                    search: `?tags=${tag.name}`,
                  }}
                >
                  {tag.name}
                </Link>
              </li>
            ))}
          </ul>
        </dd>
      </div>
    )
  })

  if (!browseCategoriesEntry && !browseTypesEntry && !tagsByTypeEntries.length)
    return null

  return (
    <Section id="combined-tags" title="Tags">
      <dl className="dl-horizontal sb-dl-tags">
        {browseCategoriesEntry}
        {tagsByTypeEntries}
        {browseTypesEntry}
        {subjectsEntry}
      </dl>
    </Section>
  )
}

const ProvenanceSection = ({ provenance = {} }) => {
  if (
    !provenance &&
    !provenance.dataSource &&
    !provenance.annotation &&
    !provenance.dateCreated
  )
    return null
  return (
    <Section id="provenance" title="Provenance">
      {provenance.dataSource ? (
        <dl className="dl-horizontal sb-dl-tags">
          <dt>Data source</dt>
          <dd>{provenance.dataSource}</dd>
        </dl>
      ) : (
        ''
      )}

      {provenance.annotation ? <div>{provenance.annotation}</div> : ''}
    </Section>
  )
}

const AdditionalInformationSection = ({ item }) => {
  let alternateTitlesSubSection
  if (item.alternateTitles && item.alternateTitles.length) {
    alternateTitlesSubSection = (
      <SubSection id="alternate-titles-subsection" title="Alternate Titles">
        <ul>
          {item.alternateTitles.map((alternateTitle, i) => (
            <li key={i}>{alternateTitle}</li>
          ))}
        </ul>
      </SubSection>
    )
  }

  let identifiersSubSection
  if (item.identifiers && item.identifiers.length) {
    let identifiersRows = item.identifiers.map((identifier, i) => {
      return (
        <tr key={i}>
          <td>{identifier.type}</td>
          <td>{identifier.scheme}</td>
          <td>{identifier.key}</td>
        </tr>
      )
    })

    identifiersSubSection = (
      <SubSection id="identifiers-subsection" title="Identifiers">
        <Table responsive bordered className="sb-table-props">
          <thead>
            <tr>
              <th>Type</th>
              <th>Scheme</th>
              <th>Key</th>
            </tr>
          </thead>
          <tbody>{identifiersRows}</tbody>
        </Table>
      </SubSection>
    )
  }

  const facets = item.facets && item.facets.length ? item.facets : []
  const nonPromotedFacets = facets.filter(
    (facet) => !PROMOTED_FACET_NAMES.includes(facet.facetName)
  )
  const nonPromotedExentensionsSubSections = nonPromotedFacets.map(
    (facet, i) => {
      return (
        <SubSection
          key={i}
          id="{facet.facetName + '_' + i"
          title={`${facet.facetName} Extension`}
        >
          <ObjectToTable object={facet} />
        </SubSection>
      )
    }
  )

  if (
    !alternateTitlesSubSection &&
    !identifiersSubSection &&
    !nonPromotedExentensionsSubSections.length
  )
    return null

  return (
    <Section id="additional-information" title="Additional Information">
      <SbExpander
        collapsedHeight={150}
        minHeightToCollapse={300}
        expanderLabel="Additional Information"
      >
        {alternateTitlesSubSection}
        {identifiersSubSection}
        {nonPromotedExentensionsSubSections}
      </SbExpander>
    </Section>
  )
}

const PROMOTED_FACET_NAMES = ['Project']
const PromotedExtensions = ({ facets }) => {
  if (!facets) return null
  const promotedFacets = facets.filter((facet) =>
    PROMOTED_FACET_NAMES.includes(facet.facetName)
  )
  return (
    <div>
      {promotedFacets.map((facet, i) => {
        return (
          <Section
            key={i}
            id={facet.facetName + '_' + i}
            title={`${facet.facetName} Extension`}
          >
            <ObjectToTable object={facet} />
          </Section>
        )
      })}
    </div>
  )
}

const EXCLUDED_OBJECT_TO_TABLE_KEYS = ['className', '_class', 'uploadedBy']

const ObjectToTable = ({ object }) => {
  if (!object) return null

  // if primitive (string, number) just output it
  if (object !== Object(object)) {
    return <div>{object}</div>
  }

  if (Array.isArray(object)) {
    return (
      <div>
        {object.map((element, i) => (
          <ObjectToTable key={i} object={element} />
        ))}
      </div>
    )
  }

  return (
    <Table responsive bordered className="sb-table-props">
      <tbody>
        {Object.keys(object)
          .filter((key) => !EXCLUDED_OBJECT_TO_TABLE_KEYS.includes(key))
          .map((key, i) => (
            <tr key={i}>
              <th scope="row">{key}</th>
              <td>
                <ObjectToTable object={object[key]} />
              </td>
            </tr>
          ))}
      </tbody>
    </Table>
  )
}
